/** @eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient } from '@angular/common/http'
import { inject } from '@angular/core'

import { buffer, firstValueFrom, Subject, takeUntil } from 'rxjs'
import { debounceTime } from 'rxjs/operators'

import { ApiService } from '@ti/core/api/services/api.service'
import { Profile } from '@ti/core/auth/services'
import { ONE_MINUTE_MS } from '@ti/core/common/constants'
import { BrowserLocalStorage } from '@ti/core/common/storage'
import { injectDestroy$ } from '@ti/core/common/utils'
import { CONFIG } from '@ti/core/contracts/config'
import { FlespiListener, SubscriptionItem } from '@ti/core/flespi-listener'

import {
  BaseDeviceTelemetry,
  CachedFlespiTokenData,
  FLESPI_TOKEN_STORAGE_KEY,
  FlespiDeviceTelemetryResponse,
  FlespiSubscriptionRef,
  FlespiTelemetryData,
  FlespiTopicMessage,
} from './contracts'

export abstract class BaseFlespiClient {
  protected readonly api = inject(ApiService)
  protected readonly http = inject(HttpClient)
  protected readonly profile = inject(Profile)
  protected readonly localStorage = inject(BrowserLocalStorage)
  protected readonly config = inject(CONFIG)
  protected readonly destroy$ = injectDestroy$()

  protected abstract readonly TELEMETRY_FIELDS: string[]

  protected readonly FLESPI_TOKEN_EXPIRE_THRESHOLD = 5 * ONE_MINUTE_MS

  protected tokenPromise?: Promise<string>

  public async getToken(forceRefresh = false): Promise<string> {
    if (forceRefresh) {
      this.tokenPromise = undefined
    }
    if (!this.tokenPromise) {
      const tokenPromise = this.obtainFlespiToken(forceRefresh)
      this.tokenPromise = tokenPromise
      // Clear cached token promise once it's settled, whether resolved or rejected -
      // otherwise a single transient failure permanently disables future token refreshes.
      tokenPromise
        .finally(() => {
          if (this.tokenPromise === tokenPromise) {
            this.tokenPromise = undefined
          }
        })
        .catch(() => {})
    }
    return this.tokenPromise
  }

  public async loadInitialTelemetryByFields(
    telemetryFields: string[],
    deviceIds?: number[],
  ): Promise<Record<string, any>[]> {
    const flespiToken = await this.getToken()
    if (!flespiToken) {
      console.warn(`Flespi token is missing!`)
      return []
    }

    const deviceSelector = deviceIds?.length ? Array.from(new Set(deviceIds)).join(',') : 'all'
    const response = await firstValueFrom(
      this.http.get<FlespiDeviceTelemetryResponse>(
        `${this.config.flespiUrl}/gw/devices/${deviceSelector}/telemetry/${telemetryFields.join(',')}`,
        { headers: { Authorization: `FlespiToken ${flespiToken}` } },
      ),
    )
    if (Array.isArray(response.result)) {
      return response.result
    } else {
      return []
    }
  }

  protected abstract loadFlespiToken(): Promise<Record<string, any>>

  /**
   * Opens an MQTT broker connection for `subscriptionItems`. `buildItems` receives a
   * connection-scoped destroy$, distinct from `this.destroy$`, so message pipelines can
   * be torn down and a fresh connection opened whenever the caller resubscribes (e.g. on
   * token rotation) without waiting for the whole client to be destroyed.
   */
  protected openSubscription(
    subscriptionName: string,
    token: string,
    buildItems: (destroy$: Subject<void>) => SubscriptionItem[],
    onAuthFailure?: () => void,
  ): FlespiSubscriptionRef {
    const destroy$ = new Subject<void>()
    const flespiBroker = this.createAndInitFlespiBroker(subscriptionName, token, onAuthFailure)
    flespiBroker.run(buildItems(destroy$))

    return {
      destroy: () => {
        destroy$.next()
        destroy$.complete()
        flespiBroker.end()
      },
    }
  }

  // Device online/offline state - identical topic and payload shape across all flespi contexts
  protected subscribeToConnectedState<T extends BaseDeviceTelemetry>(
    subscriptionName: string,
    destroy$: Subject<void>,
    onDataCb: (data: T[]) => void,
    deviceIds?: number[],
  ): SubscriptionItem {
    const isConnectedMessages$ = new Subject<FlespiTopicMessage<boolean>>()
    // Handle isConnected messages in chunks every second
    isConnectedMessages$
      .pipe(takeUntil(destroy$), buffer(isConnectedMessages$.pipe(debounceTime(1000))))
      .subscribe((messages) => {
        onDataCb(
          messages.map(
            (message) =>
              ({
                deviceId: parseInt(message.topicData.device_id),
                isConnected: Boolean(message.data),
                timestamps: {
                  isConnected: parseFloat(message.userProperties.timestamp) * 1000,
                },
              }) as T,
          ),
        )
      })

    return new SubscriptionItem({
      topicPattern: 'flespi/state/gw/devices/{device_id}/connected',
      topicParamsValues: deviceIds?.length
        ? { device_id: Array.from(new Set(deviceIds)).map(String) }
        : undefined,
      shareMode: false,
      topicFilter: '',
      sessionName: subscriptionName,
      messageHandler: (message: FlespiTopicMessage) => isConnectedMessages$.next(message),
    })
  }

  /**
   * Per-parameter telemetry topics: groups raw messages by device within each debounce
   * window and hands the accumulated payload to `mapToTelemetry` - the only step that
   * actually differs between flespi contexts.
   */
  protected subscribeToTelemetryParams<T extends BaseDeviceTelemetry>(
    subscriptionName: string,
    telemetryFields: string[],
    destroy$: Subject<void>,
    mapToTelemetry: (data: FlespiTelemetryData) => T,
    onDataCb: (data: T[]) => void,
    deviceIds?: number[],
  ): SubscriptionItem {
    const telemetryMessages$ = new Subject<FlespiTopicMessage>()
    // Handle telemetry messages in chunks every second
    telemetryMessages$
      .pipe(takeUntil(destroy$), buffer(telemetryMessages$.pipe(debounceTime(1000))))
      .subscribe((messages) => {
        const telemetryMap = new Map<number, Record<string, any>>()
        messages.forEach((message) => {
          const propertyName = message.topicData.param
          if (message.topicData.device_id && propertyName) {
            const deviceId = parseInt(message.topicData.device_id)
            let data = telemetryMap.get(deviceId)
            if (!data) {
              data = { 'device.id': deviceId }
              telemetryMap.set(deviceId, data)
            }
            data[propertyName] = {
              value: message.data,
              ts: message.userProperties.timestamp
                ? parseFloat(message.userProperties.timestamp)
                : null,
            }
          }
        })

        onDataCb(
          Array.from(telemetryMap.values()).map((data) =>
            mapToTelemetry(data as FlespiTelemetryData),
          ),
        )
      })

    return new SubscriptionItem({
      topicPattern: 'flespi/state/gw/devices/{device_id}/telemetry/{param}',
      topicParamsValues: {
        ...(deviceIds?.length ? { device_id: Array.from(new Set(deviceIds)).map(String) } : {}),
        param: Array.from(new Set(telemetryFields)),
      },
      shareMode: false,
      topicFilter: '',
      sessionName: subscriptionName,
      messageHandler: (message: FlespiTopicMessage) => telemetryMessages$.next(message),
    })
  }

  protected createAndInitFlespiBroker(
    subscriptionName: string,
    token: string,
    onAuthFailure?: () => void,
  ): FlespiListener {
    const flespiBroker = new FlespiListener()
    flespiBroker.init({
      token,
      host: 'wss://mqtt.flespi.io',
      clientPrefix: subscriptionName,
      sessionName: subscriptionName,
    })
    flespiBroker.onAuthFailure = onAuthFailure
    return flespiBroker
  }

  protected async obtainFlespiToken(forceRefresh = false) {
    let token = ''
    const cachedTokenData = forceRefresh
      ? null
      : this.localStorage.getItem(FLESPI_TOKEN_STORAGE_KEY)
    const currentUserAttributes = await firstValueFrom(this.profile.state)
    if (cachedTokenData && currentUserAttributes) {
      const tokenData: CachedFlespiTokenData = JSON.parse(cachedTokenData)
      const tokenExpiresAt = Date.now() + this.FLESPI_TOKEN_EXPIRE_THRESHOLD
      if (
        tokenData &&
        tokenData.expire * 1000 >= tokenExpiresAt &&
        tokenData.metadata.user_id === currentUserAttributes.id
      ) {
        token = tokenData.key
      }
    }
    if (!token) {
      const tokenData = await this.loadFlespiToken()
      token = tokenData.key
      this.localStorage.setItem(FLESPI_TOKEN_STORAGE_KEY, JSON.stringify(tokenData))
    }
    return token
  }
}
